import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOnboardingContext } from '../context/OnboardingContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import {
  User, Mail, Lock, Bell, Shield, Trash2,
  Camera, Save, Eye, EyeOff, Check, AlertTriangle, LogOut, Languages, Play,
  Flame, DollarSign, CreditCard, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import NotificationSettings from '../components/NotificationSettings/NotificationSettings';

export default function SettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { resetOnboarding } = useOnboardingContext();
  const [loading, setLoading] = useState(false);

  const adminRoles = ['admin', 'super_admin', 'content_admin', 'community_manager', 'moderator', 'analyst'];
  const isAdmin = user && adminRoles.includes(user.role);
  const [activeTab, setActiveTab] = useState('account');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [profile, setProfile] = useState({
    display_name: '',
    username: '',
    email: '',
    bio: '',
    profile_picture: '',
    tiktok_username: '',
    instagram_username: '',
    youtube_username: ''
  });

  const [password, setPassword] = useState({
    current: '',
    new: '',
    confirm: ''
  });


  useEffect(() => {
    if (user) {
      setProfile({
        display_name: user.display_name || '',
        username: user.username || '',
        email: user.email || '',
        bio: user.bio || '',
        profile_picture: user.profile_picture || '',
        tiktok_username: user.tiktok_username || '',
        instagram_username: user.instagram_username || '',
        youtube_username: user.youtube_username || ''
      });
    }
  }, [user]);

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      await api.put('/auth/profile', profile);
      toast.success('Profil mis à jour!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (password.new !== password.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (password.new.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/password', {
        current_password: password.current,
        new_password: password.new
      });
      toast.success('Mot de passe modifié!');
      setPassword({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/upload/image', formData);
      setProfile({ ...profile, profile_picture: response.data.url });
      toast.success('Photo uploadée!');
    } catch (error) {
      toast.error('Erreur d\'upload');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.delete('/auth/account');
      toast.success('Compte supprimé');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const tabs = [
    { id: 'account', labelKey: 'settings.account', icon: User },
    { id: 'profile', labelKey: 'settings.profile', icon: Camera },
    { id: 'security', labelKey: 'settings.security', icon: Lock },
    { id: 'notifications', labelKey: 'settings.notifications', icon: Bell },
    { id: 'language', labelKey: 'settings.language', icon: Languages },
    { id: 'help', labelKey: 'settings.help', icon: Play },
    { id: 'danger', labelKey: 'settings.dangerZone', icon: AlertTriangle },
  ];

  const languages = [
    { code: 'fr', name: 'Français', nativeName: 'Français', flag: '🇫🇷', desc: 'Interface en français' },
    { code: 'ar', name: 'العربية', nativeName: 'العربية', flag: '🇹🇳', desc: 'واجهة باللغة العربية' },
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', desc: 'Interface in English' }
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('mharher_language', langCode);
    // Set HTML direction for RTL support
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = langCode;
    // Set font based on language
    if (langCode === 'ar') {
      document.body.style.fontFamily = "'Cairo', 'Plus Jakarta Sans', sans-serif";
    } else {
      document.body.style.fontFamily = "'Plus Jakarta Sans', sans-serif";
    }
    toast.success(t('success.languageChanged'));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="settings-page">
      <h1 className="font-fraunces text-2xl font-bold text-gray-900 mb-6">{t('settings.title')}</h1>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="md:w-64 flex-shrink-0">
          <nav className="bg-white rounded-xl border border-gray-100 p-2 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#1E4ED8] text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {t(tab.labelKey)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-4">
              <h2 className="font-semibold text-lg text-gray-900">{t('settings.account')}</h2>

              {/* User summary */}
              <div className="flex items-center gap-3 p-4 bg-[#F8F5F2] rounded-xl">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {user?.username?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{user?.display_name || user?.username}</p>
                  <p className="text-sm text-gray-500">@{user?.username}</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5 bg-gradient-to-r from-[#D9381E]/10 to-[#E07A5F]/10 px-3 py-1.5 rounded-full flex-shrink-0">
                  <Flame className="w-4 h-4 text-[#D9381E]" />
                  <span className="text-sm font-bold text-[#D9381E]">{user?.barka || 0}</span>
                </div>
              </div>

              {/* Navigation shortcuts */}
              <div className="space-y-2">
                <button
                  onClick={() => navigate(`/u/${user?.username}`)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#1E4ED8]/30 hover:bg-[#1E4ED8]/5 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#1E4ED8]/10 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-[#1E4ED8]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{t('nav.profile')}</p>
                    <p className="text-sm text-gray-500">{t('settings.myProfileDesc')}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#1E4ED8] transition-colors" />
                </button>

                <button
                  onClick={() => navigate('/wallet')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-orange-200 hover:bg-orange-50/50 transition-all group"
                  data-testid="settings-wallet-link"
                >
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Flame className="w-5 h-5 text-orange-500" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{t('settings.walletBarka')}</p>
                    <p className="text-sm text-gray-500">{t('settings.walletDesc')}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                </button>

                <button
                  onClick={() => navigate('/creator')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#4F6F52]/30 hover:bg-[#4F6F52]/5 transition-all group"
                  data-testid="settings-creator-link"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#4F6F52]/10 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-[#4F6F52]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{t('settings.creatorDashboard')}</p>
                    <p className="text-sm text-gray-500">{t('settings.creatorDesc')}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#4F6F52] transition-colors" />
                </button>

                <button
                  onClick={() => navigate('/subscriptions')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#E07A5F]/30 hover:bg-[#E07A5F]/5 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E07A5F]/10 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-[#E07A5F]" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{t('common.followings')}</p>
                    <p className="text-sm text-gray-500">{t('settings.subscriptionsDesc')}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#E07A5F] transition-colors" />
                </button>

                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#D9381E]/20 hover:border-[#D9381E]/40 hover:bg-[#D9381E]/5 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#D9381E]/10 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-[#D9381E]" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-gray-900">{t('nav.admin')}</p>
                      <p className="text-sm text-gray-500">{t('settings.adminDesc')}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#D9381E] transition-colors" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
              <h2 className="font-semibold text-lg text-gray-900">{t('settings.profileInfo')}</h2>

              {/* Avatar */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="h-20 w-20 border-4 border-[#1E4ED8]/20">
                    <AvatarImage src={profile.profile_picture} />
                    <AvatarFallback className="bg-[#1E4ED8] text-white text-xl">
                      {profile.display_name?.charAt(0) || user?.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <label className="absolute bottom-0 right-0 p-1.5 bg-[#1E4ED8] rounded-full cursor-pointer hover:bg-[#1842B8] transition-colors">
                    <Camera className="w-4 h-4 text-white" />
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
                <div>
                  <p className="font-medium text-gray-900">{profile.display_name || user?.username}</p>
                  <p className="text-sm text-gray-500">@{profile.username}</p>
                </div>
              </div>

              {/* Form */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom affiché</Label>
                  <Input
                    value={profile.display_name}
                    onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
                    placeholder="Chef Mehdi"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom d'utilisateur</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                    <Input
                      value={profile.username}
                      onChange={(e) => setProfile({ ...profile, username: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Bio</Label>
                  <textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    placeholder="Parle-nous de toi..."
                    className="w-full p-3 border border-gray-200 rounded-xl resize-none h-24 focus:ring-2 focus:ring-[#1E4ED8]/20 focus:border-[#1E4ED8] transition-colors"
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-400 text-right">{profile.bio?.length || 0}/200</p>
                </div>
              </div>

              {/* Social Links */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="font-medium text-gray-900 mb-4">Réseaux sociaux</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">TikTok</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                      <Input
                        value={profile.tiktok_username}
                        onChange={(e) => setProfile({ ...profile, tiktok_username: e.target.value.replace('@', '') })}
                        className="pl-8"
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Instagram</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                      <Input
                        value={profile.instagram_username}
                        onChange={(e) => setProfile({ ...profile, instagram_username: e.target.value.replace('@', '') })}
                        className="pl-8"
                        placeholder="username"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">YouTube</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">@</span>
                      <Input
                        value={profile.youtube_username}
                        onChange={(e) => setProfile({ ...profile, youtube_username: e.target.value.replace('@', '') })}
                        className="pl-8"
                        placeholder="channel"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleProfileUpdate} disabled={loading} className="bg-[#1E4ED8]">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
              <h2 className="font-semibold text-lg text-gray-900">Sécurité du compte</h2>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Mot de passe actuel</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password.current}
                      onChange={(e) => setPassword({ ...password, current: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Nouveau mot de passe</Label>
                  <Input
                    type="password"
                    value={password.new}
                    onChange={(e) => setPassword({ ...password, new: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirmer le mot de passe</Label>
                  <Input
                    type="password"
                    value={password.confirm}
                    onChange={(e) => setPassword({ ...password, confirm: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handlePasswordChange} disabled={loading}>
                  <Lock className="w-4 h-4 mr-2" />
                  Changer le mot de passe
                </Button>
              </div>

              {/* Connected Accounts */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="font-medium text-gray-900 mb-4">Comptes connectés</h3>
                <div className="space-y-3">
                  {user?.google_id && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span className="font-medium">Google</span>
                      </div>
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Connecté
                      </span>
                    </div>
                  )}
                  {user?.facebook_id && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z"/>
                        </svg>
                        <span className="font-medium">Facebook</span>
                      </div>
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Connecté
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <NotificationSettings />
          )}

          {/* Language Tab */}
          {activeTab === 'language' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
              <h2 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <Languages className="w-5 h-5 text-[#1E4ED8]" />
                Langue / Language / اللغة
              </h2>
              
              <p className="text-gray-600 text-sm">
                Choisissez votre langue préférée pour l'interface.
              </p>

              <div className="grid gap-3">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                      i18n.language === lang.code 
                        ? 'border-[#1E4ED8] bg-[#1E4ED8]/5' 
                        : 'border-gray-200 hover:border-[#1E4ED8]/50'
                    }`}
                  >
                    <span className="text-3xl">{lang.flag}</span>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${i18n.language === lang.code ? 'text-[#1E4ED8]' : 'text-gray-900'}`}>
                        {lang.nativeName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {lang.name} - {lang.desc}
                      </p>
                    </div>
                    {i18n.language === lang.code && (
                      <Check className="w-5 h-5 text-[#1E4ED8]" />
                    )}
                  </button>
                ))}
              </div>

              <div className="bg-[#F8F5F2] p-4 rounded-xl">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> {t('settings.languageNote')}
                </p>
              </div>
            </div>
          )}

          {/* Help Tab */}
          {activeTab === 'help' && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 space-y-6">
              <h2 className="font-semibold text-lg text-gray-900 flex items-center gap-2">
                <Play className="w-5 h-5 text-[#1E4ED8]" />
                {t('settings.helpTutorial')}
              </h2>

              <div className="space-y-4">
                {/* Replay Onboarding */}
                <div className="p-4 bg-gradient-to-r from-[#1E4ED8]/5 to-[#3B82F6]/5 rounded-xl border border-[#1E4ED8]/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1E4ED8] to-[#3B82F6] flex items-center justify-center shadow-lg">
                      <span className="text-2xl">🌶️</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{t('settings.interactiveTutorial')}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('settings.discoverFeatures')}
                      </p>
                      <Button
                        onClick={resetOnboarding}
                        className="mt-4 bg-[#1E4ED8] hover:bg-[#1842B8] rounded-full"
                        data-testid="replay-onboarding-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        {t('settings.replayTutorial')}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* FAQ Links */}
                <div className="p-4 bg-[#F8F5F2] rounded-xl">
                  <h3 className="font-medium text-gray-900 mb-3">{t('settings.faq')}</h3>
                  <div className="space-y-2">
                    <a href="/support" className="block text-[#1E4ED8] hover:underline text-sm">
                      {t('settings.howToEarnBarka')}
                    </a>
                    <a href="/support" className="block text-[#1E4ED8] hover:underline text-sm">
                      {t('settings.howToPublishRecipe')}
                    </a>
                    <a href="/support" className="block text-[#1E4ED8] hover:underline text-sm">
                      {t('settings.howToParticipate')}
                    </a>
                    <a href="/support" className="block text-[#1E4ED8] hover:underline text-sm">
                      {t('settings.becomeVerified')}
                    </a>
                  </div>
                </div>

                {/* Contact Support */}
                <div className="p-4 border border-gray-200 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Mail className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">Besoin d'aide ?</h3>
                      <p className="text-sm text-gray-500">Contacte notre équipe support</p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/support')} className="rounded-full">
                      Contacter
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && (
            <div className="bg-white rounded-xl border border-red-200 p-6 space-y-6">
              <h2 className="font-semibold text-lg text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Zone Danger
              </h2>

              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Trash2 className="w-5 h-5 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-red-900">Supprimer mon compte</h3>
                      <p className="text-sm text-red-700 mt-1">
                        Cette action est irréversible. Toutes tes données, posts, recettes et BARKA seront définitivement supprimés.
                      </p>
                      <Button
                        variant="destructive"
                        className="mt-4"
                        onClick={() => setShowDeleteDialog(true)}
                      >
                        Supprimer mon compte
                      </Button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Button */}
      <div className="mt-6">
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="flex items-center justify-center gap-2 w-full py-3.5 px-4 rounded-xl border border-[#D9381E]/30 text-[#D9381E] font-semibold hover:bg-[#D9381E]/5 transition-colors"
          data-testid="logout-btn-settings"
        >
          <LogOut className="w-5 h-5" />
          {t('nav.logout')}
        </button>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer mon compte</DialogTitle>
            <DialogDescription>
              Es-tu sûr de vouloir supprimer ton compte? Cette action est irréversible et tu perdras :
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-gray-600 space-y-2 my-4">
            <li>• Tous tes posts et recettes</li>
            <li>• Tes {user?.barka || 0} points BARKA</li>
            <li>• Ton historique et badges</li>
            <li>• Ton solde créateur (si applicable)</li>
          </ul>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteAccount}>
              Oui, supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
